import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { formatToCurrency } from "shared/utils";
import { Show } from "shared/components";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { ExpandableTable } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/UniversalTable";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import {
  CombinedIncome,
  PayrollDocument,
  PayrollDocumentCoverage
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import {
  formatDate,
  formatDateRange
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/dateRange";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

const coverageLabel = (coverage?: PayrollDocumentCoverage) =>
  coverage
    ? `Paystubs from the last ${coverage.paystub_days} days · Tax documents from the last ${coverage.tax_years} tax years`
    : null;

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  "payout-statement": "Paystub",
  "W-2": "W-2",
  "1099": "1099"
};

const documentName = (row: PayrollDocument, index: number) => {
  const label = DOCUMENT_TYPE_LABELS[row.document_type];
  if (!label) return row.asset?.asset?.filename ?? `Document #${index + 1}`;

  const date = row.paystub_date ?? row.pay_period?.end_date;
  const detail = row.tax_year ?? (date ? formatDate(date) : null);

  return detail ? `${label} - ${detail}` : label;
};

// A tax form covers a year, not a pay period, so an empty range would render as "Present".
const documentDateLabel = (row: PayrollDocument) => {
  if (row.tax_year) return row.tax_year;
  if (row.pay_period?.start_date || row.pay_period?.end_date)
    return formatDateRange(row.pay_period.start_date, row.pay_period.end_date);

  return formatDate(row.paystub_date ?? row.available_date);
};

const documentColumns = [
  {
    header: "Document",
    accessor: (row: PayrollDocument, index: number) => (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-2">
            <HugeiconsIcon
              icon={File02Icon}
              size={20}
              stroke="1px"
              className="text-content-100"
            />
            <p className="text-content-1000 font-medium text-sm w-[120px] text-ellipsis whitespace-nowrap overflow-hidden">
              {documentName(row, index)}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent className="z-[50] max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
          {documentName(row, index)}
        </TooltipContent>
      </Tooltip>
    ),
    widthClass: "w-full"
  },
  {
    header: "Date range",
    accessor: (row: PayrollDocument) => (
      <p className="text-content-200 font-medium text-sm">
        {documentDateLabel(row)}
      </p>
    ),
    widthClass: "w-full"
  },
  {
    header: "Gross pay",
    accessor: (row: PayrollDocument) => (
      <p className="text-content-200 font-medium text-sm">
        {row.gross_pay ? formatToCurrency(row.gross_pay) : "-"}
      </p>
    ),
    widthClass: "w-[85px] shrink-0 text-right"
  },
  {
    header: "Net pay",
    accessor: (row: PayrollDocument) => (
      <p className="text-content-200 font-medium text-sm">
        {row.net_pay ? formatToCurrency(row.net_pay) : "-"}
      </p>
    ),
    widthClass: "w-[85px] shrink-0 text-right"
  }
];

export const PayrollDocuments = ({ income }: { income: CombinedIncome }) => {
  // Per-income documents, already filtered to this income's employment(s) by the report builder,
  // and ordered there too: paystubs first, then tax forms, each newest first.
  const documents = income.payroll_documents ?? [];

  if (documents.length === 0) return null;

  const coverage = coverageLabel(income.payroll_document_coverage);

  return (
    <div className="mt-4">
      <ContentTitle title="Documents" titleVariant="medium">
        <Show if={Boolean(coverage)}>
          <p className="text-content-300 font-medium text-sm mb-3">
            {coverage}
          </p>
        </Show>
        <ExpandableTable
          cellClassName="p-4 pl-0 text-sm self-center"
          headerRowClassName="text-content-300"
          columns={documentColumns}
          rows={documents}
          renderExpanded={(row) => (
            <div className="px-6 py-8 bg-background-900">
              <AssetItem
                url={row.asset?.asset?.url || ""}
                content_type={row.asset?.asset?.content_type || ""}
                filename={row.asset?.asset?.filename || ""}
                assetName={row.asset?.asset?.filename || ""}
              />
            </div>
          )}
        />
      </ContentTitle>
    </div>
  );
};
